import { deleteSeriesAPI, exportSeries, ReCTFFRAPI, ReGenerateDataAPI, ReSegmentationAPI } from "@api/series";
import Modal from "@components/Modal";
import { useModal } from "@components/Modal/ModalProvider";
import ResponseWaiting from "@components/ResponseWaiting/ResponseWaiting";
import useAuth from "@hooks/Auth/useAuth";
import useSaveFileToPC from "@hooks/common/useSaveFiletoPC";
import { APIError } from "@models/index";
import { AUTH_ERROR_MESSAGE } from "config/api";
import { useMutation } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { isAuthError } from "@api/api";
import { useRef } from "react";
import useModalActions from "@hooks/common/useModalAction";
import { fetchSeriesLog } from "@api/files";

export function useSeries({
  id,
  getEnabled = true,
}: {
  id: string;
  getEnabled?: boolean;
}){
  const { openModal, closeModal} = useModal();
  const { accessToken, refreshTokens} = useAuth();
  const loadingModalIdRef = useRef<string | null>(null);
  const saveFileToPC = useSaveFileToPC();
  const {
    successAction,
    errorAction,
    errorDefaultAction,
    waitingAction,
    closeLoadingModal,
  } = useModalActions();

  const isAPIError = (error: unknown): error is APIError =>
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error;

  const showErrorModal = async (error: APIError) => {
    if (isAuthError(error)) {
      await errorAction(error, AUTH_ERROR_MESSAGE, refreshTokens);
      return;
    }

    if (error.response?.status === 422) {
      await errorAction(error, "Can't Download File Type");
      return;
    }

    await errorAction(error, "Can't Download Files");
  };

  const showActionErrorModal = async (
    error: unknown,
    fallbackMessage: string,
  ) => {
    if (isAPIError(error)) {
      await errorAction(error, fallbackMessage);
      return;
    }

    if (error instanceof Error) {
      await errorDefaultAction(error.message);
      return;
    }

    await errorDefaultAction(fallbackMessage);
  };


  const { mutate: exportSeriesData } = useMutation({
    mutationFn: (seriesId: string) => exportSeries({ accessToken, id: seriesId }),
    onMutate: () => waitingAction({ message: "Downloading" }),
    onSuccess: async (res, seriesId) => {
      console.log('res:', res);
      closeLoadingModal();
      if (!res) return;
      const fileName = !isEmpty(seriesId) ? `${seriesId}-ExportData.cl` : 'ExportData.cl';
      const blob = new Blob([res.data], { type: 'application/zip' });
      await saveFileToPC(fileName, blob);
    },
    onError: async (error: APIError) => {
      closeLoadingModal();
      await showErrorModal(error);
    },
    retry: false,
  });

  const { mutate: exportSeriesLog } = useMutation({
    mutationFn: (seriesId: string) => fetchSeriesLog(accessToken,seriesId),
    onMutate: () => waitingAction({ message: "Downloading" }),
    onSuccess: async (res, seriesId) => {
      console.log('res:', res);
      closeLoadingModal();
      if (!res) return;
      const fileName = !isEmpty(seriesId) ? `${seriesId}-log.zip` : 'log.zip';
      const blob = new Blob([res.data], { type: 'application/zip' });
      await saveFileToPC(fileName, blob);
    },
    onError: async (error: APIError) => {
      closeLoadingModal();
      await showErrorModal(error);
    },
    retry: false,
  });


  const { mutateAsync: deleteSeriesDatas } = useMutation({
    mutationFn: (seriesIds: string[]) => deleteSeriesAPI({ accessToken, seriesIds }),
    onMutate: () => waitingAction({ message: "Deleting" }),
    onSuccess: async (res, seriesIds) => {
      closeLoadingModal();
    },
    onError: async (error: APIError) => {
      closeLoadingModal();
      await showActionErrorModal(error, "Can't delete series.");
    },
    retry: false,
  });

  const { mutateAsync: recalcSeriesDatas } = useMutation({
    mutationFn: (seriesIds: string[]) => ReSegmentationAPI({ accessToken, seriesIds }),
    onMutate: () => waitingAction({ message: "Recalculation" }),
    onSuccess: async (res, seriesIds) => {
      closeLoadingModal();
    },
    onError: async (error: APIError) => {
      closeLoadingModal();
      await showActionErrorModal(error, "Can't recalculate series.");
    },
    retry: false,
  });
  const { mutateAsync: reCTFFRData } = useMutation({
    mutationFn: (seriesIds: string[]) => ReCTFFRAPI({ accessToken, seriesIds }),
    onMutate: () => waitingAction({ message: "Processing CT-FFR" }),
    onSuccess: async (res, seriesIds) => {
      closeLoadingModal();
    },
    onError: async (error: APIError) => {
      closeLoadingModal();
      await showActionErrorModal(error, "Can't process CT-FFR.");
    },
    retry: false,
  });
  const { mutateAsync: reGenerateData } = useMutation({
    mutationFn: (seriesIds: string[]) => ReGenerateDataAPI({ accessToken, seriesIds }),
    onMutate: () => waitingAction({ message: "Generating Data" }),
    onSuccess: async (res, seriesIds) => {
      closeLoadingModal();
    },
    onError: async (error: APIError) => {
      closeLoadingModal();
      await showActionErrorModal(error, "Can't generate data.");
    },
    retry: false,
  });

  return {
    exportSeriesData,
    exportSeriesLog,
    deleteSeriesDatas,
    recalcSeriesDatas,
    reCTFFRData,
    reGenerateData,
  }
}
